// Géométrie de la carte territoriale, sans dépendance : emprises, masque « monde percé » du
// territoire mis en avant, projection Web Mercator dans un cadre SVG (vignettes) et chemins SVG.
// Longitude puis latitude, WGS84 (EPSG:4326).
import type { Feature, FeatureCollection, GeoJsonProperties, MultiPolygon, Polygon } from 'geojson';

export type Position = [number, number];
export type Anneau = Position[];
export type GeometriePolygonale = { type: 'Polygon'; coordinates: Anneau[] } | { type: 'MultiPolygon'; coordinates: Anneau[][] };
/** [ouest, sud, est, nord] en degrés. */
export type Emprise = [number, number, number, number];

export type NiveauTerritoire = 'pays' | 'province' | 'departement' | 'commune' | 'arrondissement';

/** Une échelle de la carte : le territoire mis en avant et, autour, ses voisins de même niveau. */
export interface Territoire {
  id: string;
  niveau: NiveauTerritoire;
  libelle: string;
  /** Null quand aucune limite libre ou officielle n'existe : la carte le dit, rien n'est dessiné. */
  geometrie: GeometriePolygonale | null;
  /** Limite sans source officielle ni libre (tracé de maquette) : affichée « indicative ». */
  indicatif: boolean;
  source: string | null;
  /** Limite chargée à la demande (fichier national volumineux) : l'échelle reste sélectionnable. */
  differe?: boolean;
  /** Autres unités du même niveau, dessinées en retrait (grisées) autour du territoire. */
  voisins?: { id: string; libelle: string; geometrie: GeometriePolygonale; indicatif: boolean }[];
}

export interface MarqueurCarte {
  id: string;
  numero: number;
  longitude: number;
  latitude: number;
  libelle: string;
  ton: 'ouvert' | 'retard' | 'autre';
}

const MONDE: Anneau = [
  [-180, -85],
  [180, -85],
  [180, 85],
  [-180, 85],
  [-180, -85],
];

/** Anneaux extérieurs d'un polygone ou multipolygone (les trous sont ignorés). */
export function anneauxExterieurs(g: GeometriePolygonale): Anneau[] {
  return g.type === 'Polygon' ? [g.coordinates[0] as Anneau] : g.coordinates.map((p) => p[0] as Anneau);
}

/** Emprise d'une géométrie ou d'un ensemble de positions. */
export function emprise(g: GeometriePolygonale | Position[]): Emprise {
  const e: Emprise = [Infinity, Infinity, -Infinity, -Infinity];
  const positions = Array.isArray(g) ? g : anneauxExterieurs(g).flat();
  for (const [x, y] of positions) {
    if (x < e[0]) e[0] = x;
    if (y < e[1]) e[1] = y;
    if (x > e[2]) e[2] = x;
    if (y > e[3]) e[3] = y;
  }
  return e;
}

/** Emprise élargie d'une proportion de chaque côté (0,1 = 10 %), jamais réduite à un point. */
export function elargirEmprise(e: Emprise, proportion: number): Emprise {
  const dx = Math.max(e[2] - e[0], 0.002) * proportion;
  const dy = Math.max(e[3] - e[1], 0.002) * proportion;
  return [e[0] - dx, e[1] - dy, e[2] + dx, e[3] + dy];
}

export function centreEmprise(e: Emprise): Position {
  return [(e[0] + e[2]) / 2, (e[1] + e[3]) / 2];
}

/**
 * Masque de la carte : un polygone couvrant le monde, percé d'un trou par anneau extérieur du
 * territoire. Tout ce qui est hors du territoire est recouvert par ce polygone (grisé).
 */
export function masque(g: GeometriePolygonale | null): Feature<Polygon> {
  const trous = g ? anneauxExterieurs(g) : [];
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [MONDE, ...trous] } };
}

/** Collection d'entités à partir de géométries et de propriétés. */
export function collection<P extends GeoJsonProperties>(elements: { geometrie: GeometriePolygonale; proprietes: P }[]): FeatureCollection<Polygon | MultiPolygon, P> {
  return { type: 'FeatureCollection', features: elements.map((e) => ({ type: 'Feature', properties: e.proprietes, geometry: e.geometrie })) };
}

/** Projection Web Mercator (EPSG:3857) d'une position, en unités du « monde » [0, 1]. */
export function mercator([longitude, latitude]: Position): Position {
  const lat = Math.max(-85.05, Math.min(85.05, latitude));
  const x = (longitude + 180) / 360;
  const y = (1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2;
  return [x, y];
}

export interface Projecteur {
  (p: Position): Position;
  echelle: number;
}

/**
 * Projecteur qui inscrit une emprise dans un cadre de largeur × hauteur pixels avec une marge,
 * en conservant les proportions (Web Mercator, centré).
 */
export function projecteur(e: Emprise, largeur: number, hauteur: number, marge = 8): Projecteur {
  const [x0, y1] = mercator([e[0], e[1]]);
  const [x1, y0] = mercator([e[2], e[3]]);
  const dx = Math.max(x1 - x0, 1e-9);
  const dy = Math.max(y1 - y0, 1e-9);
  const echelle = Math.min((largeur - 2 * marge) / dx, (hauteur - 2 * marge) / dy);
  const decalageX = (largeur - dx * echelle) / 2;
  const decalageY = (hauteur - dy * echelle) / 2;
  const f = ((p: Position): Position => {
    const [x, y] = mercator(p);
    return [Math.round(((x - x0) * echelle + decalageX) * 100) / 100, Math.round(((y - y0) * echelle + decalageY) * 100) / 100];
  }) as Projecteur;
  f.echelle = echelle;
  return f;
}

/** Chemin SVG (attribut d) d'un polygone ou multipolygone projeté, trous compris. */
export function cheminSvg(g: GeometriePolygonale, projeter: (p: Position) => Position): string {
  const polygones = g.type === 'Polygon' ? [g.coordinates] : g.coordinates;
  return polygones
    .flatMap((anneaux) => anneaux.map((a) => `M${a.map((p) => projeter(p).join(' ')).join('L')}Z`))
    .join('');
}

/** Emprise à cadrer pour une échelle : le territoire s'il a une limite, sinon ses voisins ou rien. */
export function empriseEchelle(t: Territoire): Emprise | null {
  if (t.geometrie) return elargirEmprise(emprise(t.geometrie), 0.08);
  const voisins = (t.voisins ?? []).map((v) => v.geometrie);
  if (voisins.length === 0) return null;
  const e = voisins.map(emprise).reduce<Emprise>((acc, b) => [Math.min(acc[0], b[0]), Math.min(acc[1], b[1]), Math.max(acc[2], b[2]), Math.max(acc[3], b[3])], [Infinity, Infinity, -Infinity, -Infinity]);
  return elargirEmprise(e, 0.08);
}

/** Vrai si la position est dans l'un des anneaux extérieurs (lancer de rayon). */
export function contientPosition(g: GeometriePolygonale, p: Position): boolean {
  return anneauxExterieurs(g).some((anneau) => {
    let dedans = false;
    for (let i = 0, j = anneau.length - 1; i < anneau.length; j = i, i += 1) {
      const a = anneau[i] as Position;
      const b = anneau[j] as Position;
      if (a[1] > p[1] !== b[1] > p[1] && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) dedans = !dedans;
    }
    return dedans;
  });
}

/** Numéro à deux chiffres pour les marqueurs et les listes (même numéro des deux côtés). */
export function numeroMarqueur(n: number): string {
  return String(n).padStart(2, '0');
}
